import { useTranslation } from 'react-i18next';
import { useCalendarConflicts } from '../../hooks/useCalendar';
import type { ConflictEntry, ConflictsQueryParams } from '../../types/calendar';
import { format, parseISO } from 'date-fns';

interface ConflictsAlertProps {
  queryParams?: ConflictsQueryParams;
  onConflictClick?: (conflict: ConflictEntry) => void;
}

/**
 * ConflictsAlert component
 * Displays scheduling conflicts with severity indicators and resolution suggestions
 * Click on a conflict to view details or navigate to the conflicting posts
 */
export default function ConflictsAlert({ queryParams, onConflictClick }: ConflictsAlertProps) {
  const { t } = useTranslation();

  // Fetch conflicts - default to unresolved only
  const { data, isLoading, error } = useCalendarConflicts(
    queryParams ?? { resolved: false }
  );

  /**
   * Get severity badge color
   */
  const getSeverityColor = (severity: string): string => {
    if (severity === 'error') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700';
    }
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700';
  };

  /**
   * Get severity icon
   */
  const getSeverityIcon = (severity: string): JSX.Element => {
    if (severity === 'error') {
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  /**
   * Format conflict type for display
   */
  const formatConflictType = (type: string): string => {
    switch (type) {
      case 'same_time_same_platform':
        return t('calendar.conflicts.types.sameTimeSamePlatform', 'Same time on same platform');
      case 'rate_limit_exceeded':
        return t('calendar.conflicts.types.rateLimitExceeded', 'Rate limit exceeded');
      default:
        return type.replace(/_/g, ' ');
    }
  };

  /**
   * Format scheduled time
   */
  const formatScheduledTime = (isoDate: string): string => {
    try {
      const date = parseISO(isoDate);
      return format(date, 'MMM d, h:mm a');
    } catch {
      return isoDate;
    }
  };

  /**
   * Handle conflict click
   */
  const handleConflictClick = (conflict: ConflictEntry) => {
    onConflictClick?.(conflict);
  };

  const conflicts = data?.conflicts || [];
  const summary = data?.summary;

  // Don't render anything if no conflicts
  if (!isLoading && conflicts.length === 0) {
    return null;
  }

  return (
    <div className="border-2 border-black dark:border-gray-600 rounded-lg p-6 shadow-brutal bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <svg
          className="w-5 h-5 text-yellow-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <h2 className="text-lg font-bold dark:text-gray-100">
          {t('calendar.conflicts.title', 'Scheduling Conflicts')}
        </h2>
        {summary && summary.unresolved > 0 && (
          <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
            {summary.unresolved}
          </span>
        )}
      </div>

      {/* Conflicts List */}
      <div className="max-h-[300px] overflow-y-auto space-y-3">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="h-20 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"
              />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <p className="text-red-600 dark:text-red-400 text-sm">
              {t('calendar.conflicts.error', 'Failed to load conflicts')}
            </p>
          </div>
        ) : (
          // Conflicts list
          conflicts.map((conflict) => (
            <button
              key={conflict.id}
              onClick={() => handleConflictClick(conflict)}
              className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-black dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group"
            >
              {/* Conflict header */}
              <div className="flex items-start gap-2 mb-2">
                {/* Severity badge */}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${getSeverityColor(conflict.severity)}`}
                >
                  {getSeverityIcon(conflict.severity)}
                  {conflict.severity === 'error'
                    ? t('calendar.conflicts.severity.error', 'Error')
                    : t('calendar.conflicts.severity.warning', 'Warning')}
                </span>

                {/* Platform badge if applicable */}
                {conflict.platform && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                    {conflict.platform.charAt(0).toUpperCase() + conflict.platform.slice(1)}
                  </span>
                )}
              </div>

              {/* Conflict type */}
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                {formatConflictType(conflict.conflictType)}
              </p>

              {/* Scheduled time */}
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {formatScheduledTime(conflict.scheduledAt)}
              </p>

              {/* Post count */}
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>
                  {t('calendar.conflicts.postsAffected', '{{count}} posts affected', {
                    count: conflict.posts.length,
                  })}
                </span>
              </div>

              {/* Suggestion if available */}
              {conflict.suggestion && (
                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-700 dark:text-blue-400">
                  <span className="font-medium">{t('calendar.conflicts.suggestion', 'Suggestion:')}</span>{' '}
                  {conflict.suggestion}
                </div>
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer with summary */}
      {summary && conflicts.length > 0 && (
        <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500">
            <span>
              {t('calendar.conflicts.total', '{{count}} total conflicts', {
                count: summary.total,
              })}
            </span>
            {summary.bySeverity.error > 0 && (
              <span className="text-red-500 dark:text-red-400">
                {t('calendar.conflicts.errors', '{{count}} errors', {
                  count: summary.bySeverity.error,
                })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
